import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import FarmacoService from '../../services/farmacoService';
import Button from '../common/Button';
import './Farmacos.css';

const Farmacos = () => {
  const auth = useAuth();
  const [farmacos, setFarmacos] = useState([]);
  const [filteredFarmacos, setFilteredFarmacos] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingFarmaco, setEditingFarmaco] = useState(null);
  const [deletingFarmaco, setDeletingFarmaco] = useState(null);
  const [formData, setFormData] = useState({
    components: '',
    dosis: '',
    commercialName: '',
    administrationForm: ''
  });

  const loadFarmacos = useCallback(async () => {
    try {
      setInitialLoading(true);
      const token = auth.user?.access_token;
      const response = await FarmacoService.getAllFarmacos(token);
      setFarmacos(response.data || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar los fármacos'
      });
    } finally {
      setInitialLoading(false);
    }
  }, [auth.user?.access_token]);

  // Load farmacos on component mount
  useEffect(() => {
    loadFarmacos();
  }, [loadFarmacos]);

  // Filter farmacos when search filter or farmacos change
  useEffect(() => {
    if (!searchFilter.trim()) {
      setFilteredFarmacos(farmacos);
    } else {
      const filtered = farmacos.filter(farmaco =>
        farmaco.components.toLowerCase().includes(searchFilter.toLowerCase()) ||
        farmaco.dosis.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (farmaco.commercialName || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
        (farmaco.administrationForm || '').toLowerCase().includes(searchFilter.toLowerCase())
      );
      setFilteredFarmacos(filtered);
    }
  }, [searchFilter, farmacos]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const clearSearch = () => {
    setSearchFilter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;

      if (editingFarmaco) {
        await FarmacoService.updateFarmaco(editingFarmaco.id, formData, token);
        setMessage({ type: 'success', text: 'Fármaco actualizado exitosamente' });
      } else {
        await FarmacoService.createFarmaco(formData, token);
        setMessage({ type: 'success', text: 'Fármaco creado exitosamente' });
      }

      await loadFarmacos();
      handleCloseModal();
    } catch (error) {
      setMessage({
        type: 'error',
        text: editingFarmaco ? 'Error al actualizar el fármaco' : 'Error al crear el fármaco'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (farmaco) => {
    setEditingFarmaco(farmaco);
    setFormData({
      components: farmaco.components || '',
      dosis: farmaco.dosis || '',
      commercialName: farmaco.commercialName || '',
      administrationForm: farmaco.administrationForm || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingFarmaco(null);
    setFormData({ components: '', dosis: '', commercialName: '', administrationForm: '' });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleDelete = (farmaco) => {
    setDeletingFarmaco(farmaco);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFarmaco(null);
    setFormData({ components: '', dosis: '', commercialName: '', administrationForm: '' });
    setMessage({ type: '', text: '' });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingFarmaco(null);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    if (!deletingFarmaco) return;

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      await FarmacoService.deleteFarmaco(deletingFarmaco.id, token);
      setMessage({ type: 'success', text: 'Fármaco eliminado exitosamente' });
      await loadFarmacos();
      handleCloseDeleteModal();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al eliminar el fármaco' });
    } finally {
      setIsLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="farmacos-container">
        <div className="farmacos-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="farmacos-container">
      {/* Header */}
      <div className="farmacos-header">
        <div className="header-content">
          <h1>Fármacos</h1>
          <p>Administra el inventario de medicamentos y fármacos</p>
        </div>
        <button
          className="btn-primary add-btn"
          onClick={handleAdd}
        >
          Agregar Fármaco
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="farmacos-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por componentes, dosis, nombre comercial o forma de administración..."
            value={searchFilter}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchFilter && (
            <button
              className="clear-search-btn"
              onClick={clearSearch}
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Farmacos Table */}
      <div className="farmacos-table-container">
        {filteredFarmacos.length === 0 ? (
          <div className="no-farmacos">
            <p>No se encontraron fármacos</p>
            {searchFilter && (
              <button className="btn-secondary" onClick={clearSearch}>
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="farmacos-table">
              <thead>
                <tr>
                  <th>Componentes</th>
                  <th>Dosis</th>
                  <th>Nombre Comercial</th>
                  <th>Forma de Administración</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredFarmacos.map((farmaco) => {
                  return (
                    <tr key={farmaco.id}>
                      <td className="farmaco-components">
                        <div className="farmaco-details">
                          <span className="components">{farmaco.components}</span>
                        </div>
                      </td>
                      <td className="farmaco-dosis">{farmaco.dosis}</td>
                      <td className="farmaco-commercial-name">{farmaco.commercialName || '-'}</td>
                      <td className="farmaco-administration-form">{farmaco.administrationForm || '-'}</td>
                      <td className="farmaco-actions">
                        <button
                          className="btn-secondary edit-btn"
                          onClick={() => handleEdit(farmaco)}
                          title="Editar fármaco"
                        >
                          Editar
                        </button>
                        <button
                          className="btn-danger delete-btn"
                          onClick={() => handleDelete(farmaco)}
                          title="Eliminar fármaco"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="farmacos-mobile-cards">
              {filteredFarmacos.map((farmaco) => (
                <div key={farmaco.id} className="farmaco-card">
                  <div className="farmaco-card-header">
                    <div className="farmaco-card-title">{farmaco.components}</div>
                  </div>
                  <div className="farmaco-card-content">
                    <div className="farmaco-card-row">
                      <span className="farmaco-card-label">Dosis:</span>
                      <span className="farmaco-card-value">{farmaco.dosis}</span>
                    </div>
                    <div className="farmaco-card-row">
                      <span className="farmaco-card-label">Nombre:</span>
                      <span className="farmaco-card-value">{farmaco.commercialName || '-'}</span>
                    </div>
                    <div className="farmaco-card-row">
                      <span className="farmaco-card-label">Administración:</span>
                      <span className="farmaco-card-value">{farmaco.administrationForm || '-'}</span>
                    </div>
                  </div>
                  <div className="farmaco-card-actions">
                    <button
                      className="btn-secondary edit-btn"
                      onClick={() => handleEdit(farmaco)}
                      title="Editar fármaco"
                    >
                      Editar
                    </button>
                    <button
                      className="btn-danger delete-btn"
                      onClick={() => handleDelete(farmaco)}
                      title="Eliminar fármaco"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingFarmaco ? 'Editar Fármaco' : 'Agregar Nuevo Fármaco'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="components">
                    Componentes <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="components"
                    name="components"
                    value={formData.components}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Amoxicilina, Ibuprofeno, etc."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="dosis">
                    Dosis <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="dosis"
                    name="dosis"
                    value={formData.dosis}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: 500mg, 2%, 1ml, etc."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="commercialName">Nombre Comercial</label>
                  <input
                    type="text"
                    id="commercialName"
                    name="commercialName"
                    value={formData.commercialName}
                    onChange={handleInputChange}
                    placeholder="Ej: Amoxil, Advil, etc."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="administrationForm">Forma de Administración</label>
                  <input
                    type="text"
                    id="administrationForm"
                    name="administrationForm"
                    value={formData.administrationForm}
                    onChange={handleInputChange}
                    placeholder="Ej: Oral, Intravenosa, Tópica, etc."
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  icon="fas fa-save"
                >
                  {isLoading
                    ? (editingFarmaco ? 'Actualizando...' : 'Creando...')
                    : (editingFarmaco ? 'Actualizar' : 'Crear')
                  }
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Confirmar Eliminación</h3>
              <button className="modal-close" onClick={handleCloseDeleteModal}>
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <p>
                ¿Estás seguro de que deseas eliminar el fármaco{' '}
                <strong>"{deletingFarmaco?.components}"</strong>?
              </p>
              <p className="warning-text">
                Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseDeleteModal}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Farmacos;
